import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RoomCalendarPicker = ({ 
  roomId, 
  roomName,
  initialCheckIn, 
  initialCheckOut, 
  onConfirm, 
  onCancel 
}) => {
  const { t, i18n } = useTranslation();
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Fetch prices on mount
  useEffect(() => {
    fetchRoomPrices();
  }, [roomId]);

  const fetchRoomPrices = async () => {
    setLoading(true);
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const oneYearAhead = new Date(today);
      oneYearAhead.setFullYear(oneYearAhead.getFullYear() + 1);
      
      const start = formatDate(today);
      const end = formatDate(oneYearAhead);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/rooms/${roomId}/calendar-prices`,
        {
          params: { start_date: start, end_date: end },
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      const priceMap = {};
      response.data.forEach(item => {
        priceMap[item.date] = {
          price: item.price,
          available: item.available
        };
      });
      
      setPrices(priceMap);
    } catch (error) {
      console.error('Error fetching room prices:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDisplayDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDateClick = (date) => {
    const dateStr = formatDate(date);
    const priceInfo = prices[dateStr];
    
    if (!priceInfo || !priceInfo.available) return;

    if (!checkIn || (checkIn && checkOut)) {
      setCheckIn(date);
      setCheckOut(null);
    } else if (checkIn && !checkOut) {
      if (date > checkIn) {
        // Check all dates in range are available
        const current = new Date(checkIn);
        current.setDate(current.getDate() + 1);
        let allAvailable = true;
        
        while (current < date) {
          const checkDateStr = formatDate(current);
          const checkPriceInfo = prices[checkDateStr];
          
          if (!checkPriceInfo || !checkPriceInfo.available) {
            allAvailable = false;
            break;
          }
          current.setDate(current.getDate() + 1);
        }
        
        if (allAvailable) {
          setCheckOut(date);
        } else {
          setCheckIn(date);
          setCheckOut(null);
        }
      } else {
        setCheckIn(date);
        setCheckOut(null);
      }
    }
  };

  const handleConfirm = () => {
    if (checkIn && checkOut) {
      onConfirm(formatDate(checkIn), formatDate(checkOut));
    }
  };

  const calculateNights = () => {
    if (!checkIn || !checkOut) return 0;
    const diffTime = Math.abs(checkOut - checkIn);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const isDateInRange = (date) => {
    if (!checkIn || !checkOut) return false;
    return date > checkIn && date < checkOut;
  };

  const isCheckIn = (date) => {
    return checkIn && formatDate(date) === formatDate(checkIn);
  };

  const isCheckOut = (date) => {
    return checkOut && formatDate(date) === formatDate(checkOut);
  };

  const previousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setCurrentMonth(newMonth);
  };

  const nextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    setCurrentMonth(newMonth);
  };

  const renderMonth = (monthOffset) => {
    const month = new Date(currentMonth);
    month.setMonth(month.getMonth() + monthOffset);
    
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    
    const localeMap = {
      'hr': 'hr-HR',
      'en': 'en-US',
      'it': 'it-IT',
      'de': 'de-DE'
    };
    const locale = localeMap[i18n.language] || 'hr-HR';
    const monthName = month.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const daysInMonth = lastDay.getDate();
    let startingDayOfWeek = firstDay.getDay();
    startingDayOfWeek = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const weeks = [];
    let currentWeek = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      currentWeek.push(null);
    }
    
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, monthIndex, day);
      currentWeek.push(date);
      
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }
    
    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null);
      }
      weeks.push(currentWeek);
    }
    
    const dayNames = {
      hr: ['Pon', 'Uto', 'Sri', 'Čet', 'Pet', 'Sub', 'Ned'],
      en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      de: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
      it: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
    };
    const days = dayNames[i18n.language] || dayNames['hr'];
    
    return (
      <div className="w-full">
        <div className="flex items-center justify-between mb-3">
          {monthOffset === 0 && (
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="prev-month-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {monthOffset !== 0 && <div className="w-9" />}
          
          <h3 className="text-center font-semibold text-lg flex-1">{monthName}</h3>
          
          {(monthOffset === 0 && isMobile) || (monthOffset === 1 && !isMobile) ? (
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              data-testid="next-month-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          ) : (
            <div className="w-9" />
          )}
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-1">
          {days.map((day, idx) => (
            <div key={idx} className="text-center text-xs font-medium text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>
        
        <div className="space-y-1">
          {weeks.map((week, weekIndex) => (
            <div key={weekIndex} className="grid grid-cols-7 gap-1">
              {week.map((date, dayIndex) => {
                if (!date) {
                  return <div key={dayIndex} className="aspect-square" />;
                }
                
                const dateStr = formatDate(date);
                const priceInfo = prices[dateStr] || { price: 0, available: false };
                const isAvailable = priceInfo.available;
                const inRange = isDateInRange(date);
                const isStart = isCheckIn(date);
                const isEnd = isCheckOut(date);
                const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
                
                return (
                  <div
                    key={dayIndex}
                    onClick={() => handleDateClick(date)}
                    className={`
                      aspect-square flex flex-col items-center justify-center cursor-pointer
                      relative overflow-hidden text-xs border border-gray-200
                      ${!isAvailable || isPast ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white hover:bg-gray-50'}
                    `}
                    data-testid={`date-${dateStr}`}
                  >
                    {inRange && !isStart && !isEnd && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{ backgroundColor: 'var(--color-calendar-selection)', opacity: 0.35 }}
                      />
                    )}
                    
                    {isStart && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `linear-gradient(to bottom right, transparent 50%, var(--color-calendar-selection) 50%)`
                        }}
                      />
                    )}
                    
                    {isEnd && (
                      <div 
                        className="absolute inset-0 pointer-events-none"
                        style={{
                          background: `linear-gradient(to top left, transparent 50%, var(--color-calendar-selection) 50%)`
                        }}
                      />
                    )}
                    
                    <span className="relative z-10 font-medium text-xs">
                      {date.getDate()}
                    </span>
                    
                    {isAvailable && !isPast && priceInfo.price > 0 && (
                      <span 
                        className="relative z-10 font-medium text-[9px]"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        €{priceInfo.price}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const nights = calculateNights();

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div 
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto"
        data-testid="room-calendar-modal"
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-semibold text-lg">{t('rates.selectDates')}</h3>
            <p className="text-sm text-gray-500">{roomName}</p>
          </div>
          <button 
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-calendar-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Calendar Content */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin h-8 w-8 border-4 border-[var(--color-primary)] border-r-transparent rounded-full"></div>
              <p className="mt-2 text-gray-500">{t('common.loading')}</p>
            </div>
          ) : (
            <div className={`grid gap-6 mb-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
              {renderMonth(0)}
              {!isMobile && renderMonth(1)}
            </div>
          )}
          
          {/* Selection Summary */}
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  {t('booking.checkIn')}
                </p>
                <p className="font-medium text-sm">{formatDisplayDate(checkIn)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  {t('booking.checkOut')}
                </p>
                <p className="font-medium text-sm">{formatDisplayDate(checkOut)}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  {t('booking.nights')}
                </p>
                <p className="font-medium text-sm">{nights}</p>
              </div>
            </div>
            
            {/* Confirm Button */}
            <button
              onClick={handleConfirm}
              disabled={!checkIn || !checkOut}
              className="w-full py-3 rounded-lg font-semibold uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ 
                backgroundColor: checkIn && checkOut ? 'var(--color-primary)' : '#ccc', 
                color: 'white' 
              }}
              data-testid="confirm-dates-btn"
            >
              {t('common.confirm')}
            </button>
          </div>
          
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600 mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border border-gray-200 bg-white" />
              <span>{t('home.calendar.available')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 border border-gray-200 bg-gray-100" />
              <span>{t('home.calendar.notAvailable')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 relative overflow-hidden border border-gray-200">
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom right, transparent 50%, var(--color-calendar-selection) 50%)' }} />
              </div>
              <span>{t('home.calendar.checkIn')}</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 relative overflow-hidden border border-gray-200">
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top left, transparent 50%, var(--color-calendar-selection) 50%)' }} />
              </div>
              <span>{t('home.calendar.checkOut')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoomCalendarPicker;
